import { useEffect, useRef } from 'react';
import { ROLES } from '../core/constants/roles';
import {
  buildSurgeAlertPayload,
  collectNumberSurgeAlerts,
} from '../core/rules/numberSurgeAlerts';
import { getActiveWindow } from '../core/rules/validation';
import { cubaDayKey } from '../core/time/cubaTime';
import { getServerNow } from '../services/time/serverClock';
import type { HouseConfig } from '../types';
import type { PlayLike } from '../core/rules/numberCaps';

interface UseNumberSurgeAlertsOptions {
  role: string;
  config: HouseConfig;
  lang: string;
  plays: PlayLike[];
  push: (fields: Record<string, unknown>, source?: string) => unknown;
}

/** Notifies bankers when inscription volume spikes on a number. */
export function useNumberSurgeAlerts({
  role,
  config,
  lang,
  plays,
  push,
}: UseNumberSurgeAlertsOptions) {
  const firedRef = useRef(new Set<string>());

  useEffect(() => {
    firedRef.current = new Set();
  }, [role]);

  useEffect(() => {
    if (role !== ROLES.BANKER) return;

    const now = getServerNow();
    const window = getActiveWindow(config, now);
    if (!window) return;

    const dayKey = cubaDayKey(now);
    const alerts = collectNumberSurgeAlerts(plays, config, window, dayKey);

    for (const alert of alerts) {
      if (firedRef.current.has(alert.tag)) continue;
      firedRef.current.add(alert.tag);
      push(buildSurgeAlertPayload(alert, lang), 'scheduler');
    }
  }, [role, config, lang, plays, push]);
}
